import { postingDayOf, toISODate } from '@/lib/content/content-period'
import type { PostingCadence } from '@/types/brand'

/** The two spans the calendar shows. */
export type CalendarRange = 'week' | 'month'

/** One cell of the calendar grid. */
export interface CalendarDay {
  /** `YYYY-MM-DD`, in local time. */
  readonly key: string
  readonly date: Date
  /** Whether the day belongs to the month on show; always true in a week. */
  readonly inMonth: boolean
  readonly isToday: boolean
  readonly isPast: boolean
}

/** A cadence day from today on that holds no piece yet. */
export interface OpenSlot {
  /** `YYYY-MM-DD`. */
  readonly day: string
  /** `HH:mm` in the brand's zone, or `null` when the cadence sets no time for that weekday. */
  readonly time: string | null
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)
}

/** The Monday that starts `date`'s week, at local midnight. */
export function mondayOf(date: Date): Date {
  const offset = (date.getDay() + 6) % 7
  return addDays(date, -offset)
}

function toDay(date: Date, today: Date, month: number | null): CalendarDay {
  const key = toISODate(date)
  const todayKey = toISODate(today)
  return {
    key,
    date,
    inMonth: month === null || date.getMonth() === month,
    isToday: key === todayKey,
    isPast: key < todayKey,
  }
}

/** The seven days of the week that holds `cursor`, Monday first. */
export function weekDays(cursor: Date, today: Date): CalendarDay[] {
  const monday = mondayOf(cursor)
  return Array.from({ length: 7 }, (_, index) => toDay(addDays(monday, index), today, null))
}

/** Whole weeks, Monday first, that cover the month holding `cursor`. */
export function monthDays(cursor: Date, today: Date): CalendarDay[] {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1)
  const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0)
  const start = mondayOf(first)
  const end = addDays(mondayOf(last), 6)
  const count = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1
  return Array.from({ length: count }, (_, index) =>
    toDay(addDays(start, index), today, cursor.getMonth()),
  )
}

/** The days a range shows around `cursor`. */
export function calendarDays(range: CalendarRange, cursor: Date, today: Date): CalendarDay[] {
  return range === 'week' ? weekDays(cursor, today) : monthDays(cursor, today)
}

/** Moves the cursor by `delta` weeks or months. */
export function shiftCursor(cursor: Date, range: CalendarRange, delta: number): Date {
  return range === 'week'
    ? addDays(mondayOf(cursor), delta * 7)
    : new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1)
}

/** The cadence days among `days` from today on that hold no piece, each with the cadence's time. */
export function openCadenceSlots(
  days: readonly CalendarDay[],
  cadence: PostingCadence | undefined,
  occupied: ReadonlySet<string>,
): OpenSlot[] {
  const preferred = new Set(cadence?.preferredDays ?? [])
  if (preferred.size === 0) return []
  return days.flatMap((day) => {
    if (day.isPast || occupied.has(day.key)) return []
    const weekday = postingDayOf(day.key)
    if (!preferred.has(weekday)) return []
    return [{ day: day.key, time: cadence?.preferredTimes?.[weekday] ?? null }]
  })
}

/** The local `YYYY-MM-DD` of every date given, for marking days that already hold a piece. */
export function occupiedDays(dates: readonly (Date | null)[]): Set<string> {
  return new Set(dates.flatMap((date) => (date ? [toISODate(date)] : [])))
}
